import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useRedirect } from 'react-admin';
import { Search, Flame, UserPlus, User, Building2, Loader2 } from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  useGlobalSearch,
  GlobalSearchResult,
  GlobalSearchResultType,
} from '../hooks/useGlobalSearch';

// Display order + labels + icon for each result group.
const GROUPS: {
  type: GlobalSearchResultType;
  heading: string;
  Icon: typeof Search;
}[] = [
  { type: 'funeral', heading: 'Funerals', Icon: Flame },
  { type: 'lead', heading: 'Leads', Icon: UserPlus },
  { type: 'client', heading: 'People', Icon: User },
  // Last on purpose: the RPC ranks facilities below every person, so a generic
  // term ("general", "chsld") can't push a family off the top of the list.
  { type: 'place', heading: 'Facilities', Icon: Building2 },
];

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const redirect = useRedirect();
  const { results, loading } = useGlobalSearch(open ? query : '');

  // ⌘K / Ctrl+K to open from anywhere.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Reset the query whenever the dialog closes so it opens fresh next time.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const trimmed = query.trim();
  // A short all-digits query is treated as a funeral id (matches the Enter-jump
  // guard). We surface that funeral instantly as a synthetic result so it shows
  // before the RPC returns; once the real row arrives it dedupes it away.
  const funeralIdJump =
    /^\d+$/.test(trimmed) && trimmed.length <= 6 ? trimmed : null;

  const displayResults = useMemo(() => {
    if (
      funeralIdJump &&
      !results.some((r) => r.result_id === `funeral:${funeralIdJump}`)
    ) {
      const synthetic: GlobalSearchResult = {
        result_id: `funeral:${funeralIdJump}`,
        result_type: 'funeral',
        label: `Funeral #${funeralIdJump}`,
        description: 'Open this funeral',
        url: `/funerals/${funeralIdJump}/show`,
        rank: 1,
      };
      return [synthetic, ...results];
    }
    return results;
  }, [results, funeralIdJump]);

  const grouped = useMemo(() => {
    const map = new Map<GlobalSearchResultType, GlobalSearchResult[]>();
    for (const r of displayResults) {
      const list = map.get(r.result_type) ?? [];
      list.push(r);
      map.set(r.result_type, list);
    }
    return map;
  }, [displayResults]);

  const handleSelect = (url: string) => {
    setOpen(false);
    redirect(url);
  };

  // Typing an exact funeral id (e.g. "245") and pressing Enter jumps straight to
  // that funeral's show page. Guarded to short numeric queries so it doesn't
  // hijack phone-number searches (10+ digits).
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const t = query.trim();
    if (!/^\d+$/.test(t) || t.length > 6) return;
    e.preventDefault();
    e.stopPropagation();
    const match = results.find((r) => r.result_id === `funeral:${t}`);
    handleSelect(match ? match.url : `/funerals/${t}/show`);
  };

  const showEmpty =
    !loading && trimmed.length >= 2 && displayResults.length === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="cleo-ui flex h-9 w-56 items-center gap-2 rounded-md border border-input bg-background/95 px-3 text-sm text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground sm:w-64"
        aria-label="Search clients, funerals, leads and facilities"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Search…</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:flex">
          ⌘K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput
          placeholder="Search by name, phone, email, funeral # or facility…"
          value={query}
          onValueChange={setQuery}
          onKeyDown={handleInputKeyDown}
        />
        <CommandList>
          {trimmed.length < 2 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type a name, phone number or email to search.
            </div>
          )}

          {/* Render matches immediately (incl. the synthetic funeral-id hit);
              the searching indicator sits alongside, it never hides results. */}
          {GROUPS.map(({ type, heading, Icon }) => {
            const items = grouped.get(type);
            if (!items || items.length === 0) return null;
            return (
              <CommandGroup key={type} heading={heading}>
                {items.map((item) => (
                  <CommandItem
                    key={item.result_id}
                    value={item.result_id}
                    onSelect={() => handleSelect(item.url)}
                  >
                    <Icon className="text-muted-foreground" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{item.label}</span>
                      {item.description && (
                        <span className="truncate text-xs text-muted-foreground">
                          {item.description}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          )}

          {showEmpty && <CommandEmpty>No results found.</CommandEmpty>}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalSearch;
